/**
 * Vehicle search: type "ford focus 2015" and get the wiper blades that fit.
 *
 * Shopify search cannot match 2015 against the "2011-2018" in a title, so the product list
 * (templates/collection.ww-index.liquid, rendered live) is matched in the browser instead:
 * every word has to match the title, and a year has to fall inside the title's year range.
 * A year-like word that is part of the model name (Peugeot 2008) also matches as a word.
 */
const WWVehicleSearch = (() => {
  const STOP = new Set(['wiper', 'wipers', 'blade', 'blades', 'windscreen', 'windshield', 'screen', 'for', 'my', 'the', 'a',
    'car', 'front', 'rear', 'back', 'and', 'set', 'kit', 'fits', 'fit', 'to', 'of', 'in', 'new', 'replacement', 'pair']);
  // Query spellings -> the wording used in product titles ('' drops the word).
  const ALIAS = { volkswagen: 'vw', benz: '', merc: 'mercedes', landrover: 'land rover', rangerover: 'range rover',
    chevy: 'chevrolet', alfaromeo: 'alfa romeo', beemer: 'bmw' };
  const norm = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const tokens = (query) =>
    norm(query).split(' ').flatMap((w) => (w in ALIAS ? ALIAS[w].split(' ') : [w])).filter((w) => w && !STOP.has(w));

  // "WipeWave Front and Rear Wiper Blades for VW Golf 1992-1997 Hatchback, 21in 19in Front + 13in Rear, Set of 3, U-Hook Fit"
  // -> { car: 'VW Golf 1992-1997 Hatchback', sizes: [['Front', '21" 19"'], ['Rear', '13"']], fit: 'U-Hook Fit' }; null if no sizes.
  const spec = (title) => {
    const m = title.match(/^WipeWave (.+?) Wiper Blades? for (.+)$/);
    if (!m) return null;
    const parts = m[2].split(', ');
    const i = parts.findIndex((p) => /^\d+in\b/.test(p));
    if (i < 1) return null;
    const inch = (s) => s.replace(/(\d+)in\b/g, '$1"');
    const both = parts[i].match(/^(.+) Front \+ (.+) Rear$/);
    const sizes = both ? [['Front', inch(both[1])], ['Rear', inch(both[2])]] : [[/^rear$/i.test(m[1]) ? 'Rear' : 'Front', inch(parts[i])]];
    const fit = parts.slice(i + 1).filter((p) => !/^(Pair|Set of \d+)$/.test(p)).join(', ');
    return { car: parts.slice(0, i).join(', '), sizes, fit };
  };

  const item = ([title, url, img, price, variantTitle]) => {
    const t = title.replace(/\s+–\s+WipeWave$/, '');
    // "2011-2018", also "2009 (59 reg only)-2011"
    const y = t.match(/\b((?:19|20)\d\d)(?:\s*\([^)]*\))?\s*-\s*((?:19|20)\d\d)\b/);
    const hay = norm(t);
    return { t, url, img, price, spec: spec(variantTitle || t), from: y ? +y[1] : 0, to: y ? +y[2] : 0,
      words: hay.split(' '), flat: hay.replace(/ /g, '') };
  };

  const match = (items, query) => {
    const words = tokens(query);
    if (!words.length) return [];
    const out = [];
    for (const it of items) {
      let score = 0;
      const ok = words.every((w) => {
        const exact = it.words.includes(w);
        score += exact;
        if (/^(19|20)\d\d$/.test(w)) return exact || !it.from || (it.from <= +w && +w <= it.to);
        return exact || it.words.some((x) => x.startsWith(w)) || (w.length > 2 && it.flat.includes(w));
      });
      if (ok) out.push([score, it]);
    }
    return out.sort((a, b) => b[0] - a[0] || a[1].t.localeCompare(b[1].t)).map(([, it]) => it);
  };

  return { item, match, spec };
})();

if (typeof module === 'object') module.exports = WWVehicleSearch;

(() => {
  if (typeof document === 'undefined') return;
  const INDEX = document.currentScript && document.currentScript.dataset.index;
  if (!INDEX) return;

  let items;
  const load = () =>
    (items ||= (async () => {
      const get = (page) => fetch(`${INDEX}&page=${page}`).then((r) => r.json());
      const first = await get(1);
      const rest = await Promise.all(Array.from({ length: first.pages - 1 }, (_, i) => get(i + 2)));
      return [first, ...rest].flatMap((p) => p.items).map(WWVehicleSearch.item);
    })().catch((e) => {
      items = null; // retry on the next keystroke
      throw e;
    }));

  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const row = (it) =>
    `<li class="ww-vs__item"><a href="${esc(it.url)}">` +
    (it.img ? `<img src="${esc(it.img)}" alt="" width="96" height="96">` : '') +
    '<span class="ww-vs__body">' +
    (it.spec
      ? `<span class="ww-vs__fits">Fits</span><span class="ww-vs__car">${esc(it.spec.car)}</span>` +
        it.spec.sizes.map(([k, v]) => `<span class="ww-vs__size"><b>${k}:</b> ${esc(v)}</span>`).join('') +
        (it.spec.fit ? `<span class="ww-vs__fit">${esc(it.spec.fit)}</span>` : '')
      : `<span class="ww-vs__title">${esc(it.t)}</span>`) +
    `<span class="ww-vs__price">${esc(it.price)}</span></span></a></li>`;

  // Search box with instant results (homepage).
  for (const form of document.querySelectorAll('[data-ww-vs]')) {
    const input = form.querySelector('input[name="q"]');
    const list = form.querySelector('.ww-vs__list');
    const status = form.querySelector('.ww-vs__status');
    const close = () => {
      list.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    };
    const update = async () => {
      const q = input.value.trim();
      if (q.length < 2) return close();
      let found;
      try {
        found = WWVehicleSearch.match(await load(), q);
      } catch (e) {
        return close(); // the form still submits to Shopify search
      }
      if (input.value.trim() !== q) return; // a newer keystroke is being handled
      const all = `${form.action}?q=${encodeURIComponent(q)}&type=product`;
      list.innerHTML = found.length
        ? found.slice(0, 6).map(row).join('') + `<li><a class="ww-vs__all" href="${esc(all)}">See all ${plural(found.length, 'result')} &rsaquo;</a></li>`
        : '<li class="ww-vs__empty">No exact match. Try only the make and model, or choose your make below.</li>';
      status.textContent = plural(found.length, 'matching product');
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };
    input.addEventListener('focus', () => load().catch(() => {}), { once: true });
    input.addEventListener('input', update);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' && !list.hidden) {
        e.preventDefault();
        list.querySelector('a')?.focus();
      }
      if (e.key === 'Escape') close();
    });
    list.addEventListener('keydown', (e) => {
      const links = [...list.querySelectorAll('a')];
      const i = links.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        links[Math.min(i + 1, links.length - 1)].focus();
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        (i > 0 ? links[i - 1] : input).focus();
      }
      if (e.key === 'Escape') {
        close();
        input.focus();
      }
    });
    document.addEventListener('click', (e) => form.contains(e.target) || close());
  }

  // Search results page: vehicle matches replace Shopify's results, which stay one click away.
  const page = document.querySelector('[data-ww-vs-page]');
  if (!page || !page.dataset.q) return;
  load()
    .then((all) => {
      const found = WWVehicleSearch.match(all, page.dataset.q);
      if (!found.length) return;
      const main = document.querySelector('[id^="shopify-section-"][id$="__main"]');
      page.innerHTML =
        `<h2 class="ww-vs__heading">${plural(found.length, 'product')} for &ldquo;${esc(page.dataset.q)}&rdquo;</h2>` +
        `<ul class="ww-vs__list ww-vs__list--page">${found.map(row).join('')}</ul>` +
        (main ? '<button type="button" class="ww-vs__more">Show all store search results</button>' : '');
      page.hidden = false;
      if (!main) return;
      main.hidden = true;
      page.querySelector('.ww-vs__more').addEventListener('click', (e) => {
        main.hidden = false;
        e.target.remove();
      });
    })
    .catch(() => {});
})();
